import * as Yup from "yup";

/*
 * Registro dos campos que uma atualizacao cadastral pode exigir.
 * Espelha o enum CampoAtualizacaoCadastral do backend (conciliador-jsf); as regras daqui
 * existem para dar retorno imediato ao usuario — a validacao definitiva e sempre a do servidor.
 *
 * Para incluir um campo novo: acrescente a constante no backend e a entrada em CAMPOS abaixo.
 */

/** Devolve a mensagem de erro ou null quando o valor e valido. */
type Validador = (valor: string | null | undefined) => string | null;

export interface DefinicaoCampo {
  tipoInput: "email" | "tel" | "text";
  /** Campo exibido como cidade + UF (dois inputs para um unico valor "NOME - UF"). */
  cidadeUf?: boolean;
  placeholder: string;
  /** Colunas ocupadas no grid de 12 a partir de telas medias; em telas pequenas todos ocupam a linha inteira. */
  largura: 4 | 6 | 8 | 12;
  /** Dica de preenchimento automatico do navegador. */
  autoComplete: string;
  tamanhoMaximo: number;
  /** Aplicado a cada digitacao (mascara). */
  formatar: (valor: string) => string;
  /** Regra do campo preenchido; campo vazio so e aceito quando o servidor o marca como opcional. */
  validar: Validador;
}

const TAMANHO_MAXIMO_EMAIL = 100;

// Mesmo padrao do RegraEmail (o valor e comparado em minusculas).
const FORMATO_EMAIL = /^[a-z0-9!#$%&'*+/=?^_`{|}~-]+(\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@([a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,}$/;

const DDDS_VALIDOS = new Set([
  "11", "12", "13", "14", "15", "16", "17", "18", "19",
  "21", "22", "24", "27", "28",
  "31", "32", "33", "34", "35", "37", "38",
  "41", "42", "43", "44", "45", "46", "47", "48", "49",
  "51", "53", "54", "55",
  "61", "62", "63", "64", "65", "66", "67", "68", "69",
  "71", "73", "74", "75", "77", "79",
  "81", "82", "83", "84", "85", "86", "87", "88", "89",
  "91", "92", "93", "94", "95", "96", "97", "98", "99",
]);

export const somenteDigitos = (valor: string | null | undefined) => (valor || "").replace(/\D/g, "");

/** Remove o zero de discagem/operadora ("011...") — DDD nunca comeca com zero. */
const digitosTelefone = (valor: string | null | undefined) => somenteDigitos(valor).replace(/^0+/, "");

/**
 * Mascara progressiva: (DD) NNNN-NNNN para fixo e (DD) NNNNN-NNNN para celular,
 * decidida pela quantidade de digitos ja informada.
 */
export const formatarTelefone = (valor: string | null | undefined) => {
  const digitos = digitosTelefone(valor).slice(0, 11);
  if (digitos.length === 0) {
    return "";
  }
  if (digitos.length <= 2) {
    return `(${digitos}`;
  }
  const ddd = digitos.slice(0, 2);
  const numero = digitos.slice(2);
  if (numero.length <= 4) {
    return `(${ddd}) ${numero}`;
  }
  const corte = numero.length === 9 ? 5 : 4;
  return `(${ddd}) ${numero.slice(0, corte)}-${numero.slice(corte)}`;
};

const todosDigitosIguais = (numero: string) => numero.split("").every((d) => d === numero[0]);

/**
 * Validacao comum de telefone (mesma ordem de mensagens do RegraNumeroTelefone).
 * Devolve a mensagem de erro ou null quando o valor e valido.
 */
const validarTelefone = (rotulo: string, validarNumero: (numero: string) => string | null) => (
  valor: string | null | undefined
): string | null => {
  const digitos = digitosTelefone(valor);
  const rotuloMaiusculo = rotulo.charAt(0).toUpperCase() + rotulo.slice(1);
  if (digitos.length === 0) {
    return `Informe o ${rotulo}.`;
  }
  if (digitos.length < 10 || digitos.length > 11) {
    return `${rotuloMaiusculo} deve ter DDD + número.`;
  }
  const ddd = digitos.slice(0, 2);
  const numero = digitos.slice(2);
  if (!DDDS_VALIDOS.has(ddd)) {
    return `DDD ${ddd} inválido.`;
  }
  if (todosDigitosIguais(numero)) {
    return `${rotuloMaiusculo} inválido.`;
  }
  return validarNumero(numero);
};

/** Fixo (8 digitos iniciando em 2 a 5) ou celular (9 digitos iniciando em 9). */
const numeroFixoOuCelular = (numero: string) =>
  (numero.length === 8 && numero[0] >= "2" && numero[0] <= "5") || (numero.length === 9 && numero[0] === "9");

export const validarTelefoneContato = validarTelefone("telefone", (numero) =>
  numeroFixoOuCelular(numero) ? null : "Telefone inválido."
);

/** Aceita tambem fixo: ha usuarios que informam o mesmo numero do telefone. */
export const validarCelular = validarTelefone("celular", (numero) =>
  numeroFixoOuCelular(numero) ? null : "Celular inválido."
);

export const validarEmail = (valor: string | null | undefined): string | null => {
  const email = (valor || "").trim().toLowerCase();
  if (email.length === 0) {
    return "Informe o e-mail.";
  }
  if (email.length > TAMANHO_MAXIMO_EMAIL) {
    return `O e-mail deve ter no máximo ${TAMANHO_MAXIMO_EMAIL} caracteres.`;
  }
  if (/[;, ]/.test(email)) {
    return "Informe apenas um e-mail.";
  }
  return FORMATO_EMAIL.test(email) ? null : "E-mail inválido.";
};

export const formatarCep = (valor: string | null | undefined) => {
  const digitos = somenteDigitos(valor).slice(0, 8);
  return digitos.length > 5 ? `${digitos.slice(0, 5)}-${digitos.slice(5)}` : digitos;
};

/** Mesmas mensagens do RegraCep. */
export const validarCep = (valor: string | null | undefined): string | null => {
  const digitos = somenteDigitos(valor);
  if (digitos.length === 0) {
    return "Informe o CEP.";
  }
  if (digitos.length !== 8) {
    return "CEP deve ter 8 dígitos.";
  }
  return /^0+$/.test(digitos) ? "CEP inválido." : null;
};

const normalizarTexto = (valor: string | null | undefined) => (valor || "").trim().replace(/\s+/g, " ");

/**
 * Texto livre (nome, endereco), com as mensagens do RegraTexto.
 * @param rotulo em minusculas e com artigo ("o bairro")
 */
export const validarTexto = (rotulo: string, tamanhoMaximo: number): Validador => (valor) => {
  const texto = normalizarTexto(valor);
  if (texto.length === 0) {
    return `Informe ${rotulo}.`;
  }
  if (texto.length > tamanhoMaximo) {
    const semArtigo = rotulo.slice(rotulo.indexOf(" ") + 1);
    return `${semArtigo.charAt(0).toUpperCase()}${semArtigo.slice(1)} deve ter no máximo ${tamanhoMaximo} caracteres.`;
  }
  return null;
};

const TAMANHO_MAXIMO_CIDADE = 50;

const UFS = new Set([
  "AC", "AL", "AP", "AM", "BA", "CE", "DF", "ES", "GO", "MA", "MT", "MS", "MG", "PA",
  "PB", "PR", "PE", "PI", "RJ", "RN", "RS", "RO", "RR", "SC", "SP", "SE", "TO",
]);

/**
 * Separa "NOME - UF" como o RegraCidadeUf: a UF e o que vem depois do ultimo hifen, se tiver ate 2 letras
 * ("Embu-Guaçu" e uma cidade sem UF). Sem UF, o texto todo e a cidade.
 */
export const separarCidadeUf = (valor: string | null | undefined) => {
  const texto = normalizarTexto(valor);
  const partes = /^(.*?)\s*-\s*([A-Za-zÀ-ÿ]{0,2})$/.exec(texto);
  return partes ? { cidade: partes[1].trim(), uf: partes[2].toUpperCase() } : { cidade: texto, uf: "" };
};

/** Mesmas mensagens do RegraCidadeUf. */
export const validarCidade = (valor: string | null | undefined): string | null => {
  const { cidade, uf } = separarCidadeUf(valor);
  if (cidade.length === 0) {
    return "Informe a cidade.";
  }
  if (uf.length === 0) {
    return "Informe a UF.";
  }
  if (!UFS.has(uf)) {
    return "UF inválida.";
  }
  return cidade.length > TAMANHO_MAXIMO_CIDADE
    ? `Cidade deve ter no máximo ${TAMANHO_MAXIMO_CIDADE} caracteres.`
    : null;
};

/** Adapta um Validador para Yup, mantendo a mensagem especifica de cada regra. */
const comoYup = (validar: Validador) =>
  Yup.string().test("regra-campo", "", function(valor: string | null | undefined) {
    const mensagem = validar(valor);
    return mensagem === null ? true : this.createError({ message: mensagem });
  });

const semMascara = (valor: string) => valor;

export const CAMPOS: { [campo: string]: DefinicaoCampo } = {
  NOME: {
    tipoInput: "text",
    placeholder: "Nome completo ou razão social",
    largura: 12,
    autoComplete: "name",
    tamanhoMaximo: 100,
    formatar: semMascara,
    validar: validarTexto("o nome", 100),
  },
  EMAIL: {
    tipoInput: "email",
    placeholder: "nome@empresa.com.br",
    largura: 12,
    autoComplete: "email",
    tamanhoMaximo: TAMANHO_MAXIMO_EMAIL,
    formatar: semMascara,
    validar: validarEmail,
  },
  TELEFONE: {
    tipoInput: "tel",
    placeholder: "(00) 0000-0000",
    largura: 6,
    autoComplete: "tel",
    tamanhoMaximo: 15,
    formatar: formatarTelefone,
    validar: validarTelefoneContato,
  },
  CELULAR: {
    tipoInput: "tel",
    placeholder: "(00) 00000-0000",
    largura: 6,
    autoComplete: "tel",
    tamanhoMaximo: 15,
    formatar: formatarTelefone,
    validar: validarCelular,
  },
  CEP: {
    tipoInput: "text",
    placeholder: "00000-000",
    largura: 4,
    autoComplete: "postal-code",
    tamanhoMaximo: 9,
    formatar: formatarCep,
    validar: validarCep,
  },
  ENDERECO: {
    tipoInput: "text",
    placeholder: "Rua, avenida...",
    largura: 8,
    autoComplete: "address-line1",
    tamanhoMaximo: 50,
    formatar: semMascara,
    validar: validarTexto("o logradouro", 50),
  },
  NUMERO: {
    tipoInput: "text",
    placeholder: "",
    largura: 4,
    autoComplete: "off",
    tamanhoMaximo: 10,
    formatar: semMascara,
    validar: validarTexto("o número", 10),
  },
  COMPLEMENTO: {
    tipoInput: "text",
    placeholder: "Sala, bloco...",
    largura: 8,
    autoComplete: "address-line2",
    tamanhoMaximo: 25,
    formatar: semMascara,
    validar: validarTexto("o complemento", 25),
  },
  BAIRRO: {
    tipoInput: "text",
    placeholder: "",
    largura: 6,
    autoComplete: "off",
    tamanhoMaximo: 50,
    formatar: semMascara,
    validar: validarTexto("o bairro", 50),
  },
  CIDADE: {
    tipoInput: "text",
    cidadeUf: true,
    placeholder: "",
    largura: 6,
    autoComplete: "off",
    tamanhoMaximo: 60,
    formatar: semMascara,
    validar: validarCidade,
  },
};

/** Campo que o servidor pediu mas o front ainda nao conhece: aceita texto livre e deixa o servidor validar. */
const CAMPO_GENERICO: DefinicaoCampo = {
  tipoInput: "text",
  placeholder: "",
  largura: 12,
  autoComplete: "off",
  tamanhoMaximo: 255,
  formatar: semMascara,
  validar: (valor) => (normalizarTexto(valor).length === 0 ? "Campo obrigatório." : null),
};

export const definicaoCampo = (campo: string): DefinicaoCampo => CAMPOS[campo] || CAMPO_GENERICO;

export const valoresIniciais = (campos: Array<{ campo: string; valorAtual: string | null }>) =>
  campos.reduce<{ [campo: string]: string }>((valores, { campo, valorAtual }) => {
    valores[campo] = definicaoCampo(campo).formatar(valorAtual || "");
    return valores;
  }, {});

/** Campo opcional (definido pelo servidor) aceita ficar vazio; preenchido, segue a regra do campo. */
const opcional = (validar: Validador): Validador => (valor) =>
  normalizarTexto(valor).length === 0 ? null : validar(valor);

export const schemaValidacao = (campos: Array<{ campo: string; obrigatorio?: boolean | null }>) =>
  Yup.object().shape(
    campos.reduce<{ [campo: string]: Yup.StringSchema }>((schema, { campo, obrigatorio }) => {
      const { validar } = definicaoCampo(campo);
      schema[campo] = comoYup(obrigatorio === false ? opcional(validar) : validar);
      return schema;
    }, {})
  );
